import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n';
import './ChatContactForm.scss';

/**
 * Componente de formulario de contacto
 * Permite al usuario enviar su email y motivo de consulta
 */
const ChatContactForm = ({
  onSubmit,
  onCancel,
  initialEmail = '',
  initialReason = '',
  isSubmitting = false,
  error = null,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [reason, setReason] = useState(initialReason);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const MIN_REASON_LENGTH = 10;
  const MAX_REASON_LENGTH = 500;

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const validateReason = (value) => {
    return value.trim().length > 0;
  };

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    if (field === 'email') {
      if (!value) {
        newErrors.email = I18n.t('chatbot.contact.errorEmailRequired');
      } else if (!validateEmail(value)) {
        newErrors.email = I18n.t('chatbot.contact.errorEmail');
      } else {
        delete newErrors.email;
      }
    }

    if (field === 'reason') {
      if (!value || !validateReason(value)) {
        newErrors.reason = I18n.t('chatbot.contact.errorReasonRequired');
      } else {
        delete newErrors.reason;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    validateField(field, field === 'email' ? email : reason);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Marcar todos los campos como tocados
    setTouched({ email: true, reason: true });

    // Validar ambos campos
    const emailValid = validateField('email', email);
    const reasonValid = validateField('reason', reason);

    if (emailValid && reasonValid) {
      onSubmit({ email, reason });
    }
  };

  const getRemainingChars = () => MAX_REASON_LENGTH - reason.length;
  const isCharLimitClose = () => getRemainingChars() < 50;

  return (
    <div className="chat-contact-form">
      <div className="chat-contact-form__header">
        <h3 className="chat-contact-form__title">
          {I18n.t('chatbot.contact.title')}
        </h3>
      </div>

      <form className="chat-contact-form__form" onSubmit={handleSubmit}>
        <div className="chat-contact-form__field">
          <label htmlFor="contact-email" className="chat-contact-form__label">
            {I18n.t('chatbot.contact.emailLabel')}
            <span className="chat-contact-form__required">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            className={`chat-contact-form__input ${
              touched.email && errors.email
                ? 'chat-contact-form__input--error'
                : ''
            }`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleBlur('email')}
            placeholder={I18n.t('chatbot.contact.emailPlaceholder')}
          />
          {touched.email && errors.email && (
            <span className="chat-contact-form__error">{errors.email}</span>
          )}
        </div>

        <div className="chat-contact-form__field">
          <label htmlFor="contact-reason" className="chat-contact-form__label">
            {I18n.t('chatbot.contact.reasonLabel')}
            <span className="chat-contact-form__required">*</span>
          </label>
          <textarea
            id="contact-reason"
            className={`chat-contact-form__textarea ${
              touched.reason && errors.reason
                ? 'chat-contact-form__textarea--error'
                : ''
            }`}
            value={reason}
            onChange={(e) => {
              if (e.target.value.length <= MAX_REASON_LENGTH) {
                setReason(e.target.value);
              }
            }}
            onBlur={() => handleBlur('reason')}
            placeholder={I18n.t('chatbot.contact.reasonPlaceholder')}
            rows={4}
            maxLength={MAX_REASON_LENGTH}
          />
          {touched.reason && errors.reason && (
            <span className="chat-contact-form__error">{errors.reason}</span>
          )}
        </div>

        {error && (
          <div className="chat-contact-form__api-error">
            <span className="material-symbols-rounded">error</span>
            {error}
          </div>
        )}

        <div className="chat-contact-form__actions">
          <button
            type="button"
            className="chat-contact-form__btn chat-contact-form__btn--cancel"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancelar formulario de contacto"
          >
            <span className="material-symbols-rounded">close</span>
            {I18n.t('chatbot.contact.cancel')}
          </button>
          <button
            type="submit"
            className="chat-contact-form__btn chat-contact-form__btn--submit"
            disabled={isSubmitting || Object.keys(errors).length > 0}
            aria-label="Enviar formulario de contacto"
          >
            {isSubmitting ? (
              <>
                <span className="material-symbols-rounded spinning">sync</span>
                {I18n.t('chatbot.contact.sending')}
              </>
            ) : (
              <>
                <span className="material-symbols-rounded">send</span>
                {I18n.t('chatbot.contact.submit')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

ChatContactForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialEmail: PropTypes.string,
  initialReason: PropTypes.string,
  isSubmitting: PropTypes.bool,
  error: PropTypes.string,
};

export default ChatContactForm;
